import { PRECIP_CLASSES_MM_H, precipClass } from "@divespots/contracts";

/** Red, green, blue (0-255) and alpha (0-255). */
export type Rgba = readonly [number, number, number, number];

/** Only reached when a token cannot be read at all (it is never a design colour): visibly wrong, not grey. */
const FALLBACK: readonly Rgba[] = [
  [0, 0, 0, 0],
  [0, 0, 0, 0],
  [0, 0, 0, 0],
  [0, 0, 0, 0],
];

export const precipVar = (c: 0 | 1 | 2 | 3): string => `--precip-${c + 1}`;

/**
 * A design token as the browser hands it back: `rgba(r, g, b, a)`, `rgb(r g b / a)` or, after the
 * build's CSS minifier, `#rgb` / `#rrggbb` / `#rrggbbaa`. Null for anything else.
 */
export function parseColor(css: string): Rgba | null {
  const hex = /^#([\da-f]{3,4}|[\da-f]{6}|[\da-f]{8})$/i.exec(css.trim());
  if (hex) {
    let h = hex[1]!;
    if (h.length <= 4) h = [...h].map((c) => c + c).join("");
    const n = (i: number) => parseInt(h.slice(i, i + 2), 16);
    return [n(0), n(2), n(4), h.length === 8 ? n(6) : 255];
  }
  const m = /^rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)(?:\s*[,/]\s*([\d.]+%?))?\s*\)$/i.exec(
    css.trim(),
  );
  if (!m) return null;
  const a = m[4] === undefined ? 1 : m[4].endsWith("%") ? parseFloat(m[4]) / 100 : parseFloat(m[4]);
  return [Number(m[1]), Number(m[2]), Number(m[3]), Math.round(a * 255)];
}

/** The four class colours from `--precip-1..4` (the lookup is injected so tests need no DOM). */
export function precipColors(read: (name: string) => string): Rgba[] {
  return ([0, 1, 2, 3] as const).map((c) => parseColor(read(precipVar(c))) ?? FALLBACK[c]!);
}

/**
 * Just above the 0.1 mm/h edge the fill fades in (20% to 100% of the token alpha between 0.1 and
 * 0.4 mm/h), so the boundary of a rain field is soft instead of a stair of single pixels.
 */
export function edgeFade(mmPerHour: number): number {
  return 0.2 + 0.8 * Math.min(1, Math.max(0, (mmPerHour - PRECIP_CLASSES_MM_H[0]) / 0.3));
}

/** The class colour for mm/h; null (transparent) where there is no value or it is below 0.1 mm/h. */
export function colorAt(mmPerHour: number | null, colors: readonly Rgba[]): Rgba | null {
  if (mmPerHour === null) return null;
  const c = precipClass(mmPerHour);
  const base = c < 0 ? null : colors[c];
  if (!base) return null;
  return [base[0], base[1], base[2], Math.max(1, Math.round(base[3] * edgeFade(mmPerHour)))];
}

/**
 * One RGBA pixel per cell of a `w` x `h` raster; `at` maps a cell centre (in 0..w, 0..h) to the
 * point to sample. Cells without a value stay fully transparent. Returns the number drawn.
 */
export function rasterize(
  sample: (x: number, y: number) => number | null,
  w: number,
  h: number,
  colors: readonly Rgba[],
): { data: Uint8ClampedArray<ArrayBuffer>; drawn: number; byClass: number[] } {
  const data = new Uint8ClampedArray(w * h * 4);
  let drawn = 0;
  const byClass = [0, 0, 0, 0];
  for (let y = 0; y < h; y++)
    for (let x = 0; x < w; x++) {
      const mm = sample(x + 0.5, y + 0.5);
      const c = colorAt(mm, colors);
      if (!c) continue;
      byClass[precipClass(mm!)]!++;
      const o = (y * w + x) * 4;
      data[o] = c[0];
      data[o + 1] = c[1];
      data[o + 2] = c[2];
      data[o + 3] = c[3];
      drawn++;
    }
  return { data, drawn, byClass };
}

/** Coarse raster size for a viewport of `kmW` x `kmH` km at about one pixel per km, bounded for phones. */
export function rasterSize(kmW: number, kmH: number): { w: number; h: number } {
  const clamp = (km: number) => Math.min(360, Math.max(8, Math.round(km)));
  return { w: clamp(kmW), h: clamp(kmH) };
}
